/**
 * @description Admin API service for Lynx Admin portal
 *
 * Key features:
 * - Tenant CRUD (list, get, create, update, deactivate)
 * - Channel Partner list / get / create / sending-domain (LYN-288 / LYN-156 / LYN-308)
 * - Signup capabilities PATCH on that partner + under-CP SO (LYN-320)
 * - Switch-into overlay start / stop / up (LYN-157) — /api/switch, not /api/admin
 * - Tenant features and branding management (features payload supports boolean
 *   or { isEnabled, configuration } objects for CMS audience toggles)
 * - Merchant listing per tenant
 * - Direct LynxPay CMS merchants + hosting_tier flip (LYN-76)
 * - Template listing for tenant creation
 *
 * @dependencies
 * - apiClient: Axios HTTP client with auth interceptors
 *
 * @notes
 * - All endpoints require superadmin auth (enforced by backend middleware)
 * - Mounted at /api/admin/* on the backend
 * - Response shape: { success: boolean, data: object|array, meta?: object }
 *   (CMS direct-merchants also returns pagination: { total, limit, offset, hasMore })
 * - Errors are NOT caught here — they propagate to calling components
 *   where they can be handled with full context (error codes, UI state)
 * - 401 handling is in AuthContext's interceptor (auto-refresh + retry)
 */

import apiClient from './apiClient';

// ── Tenants ──────────────────────────────────────────────

export const getTenants = () =>
  apiClient.get('/admin/tenants').then(r => r.data);

export const getTenant = (id) =>
  apiClient.get(`/admin/tenants/${id}`).then(r => r.data);

export const createTenant = (data, idempotencyKey) =>
  apiClient.post('/admin/tenants', data, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
  }).then(r => r.data);

export const updateTenant = (id, updates) =>
  apiClient.put(`/admin/tenants/${id}`, updates).then(r => r.data);

export const deactivateTenant = (id) =>
  apiClient.patch(`/admin/tenants/${id}/deactivate`).then(r => r.data);

export const sendTenantInvite = (id) =>
  apiClient.post(`/admin/tenants/${id}/invite`).then(r => r.data);

export const sendChannelPartnerInvite = (id) =>
  apiClient.post(`/admin/channel-partners/${id}/invite`).then(r => r.data);

export const getChannelPartnerEmailSendingDomain = (channelPartnerId) =>
  apiClient.get(`/admin/channel-partners/${channelPartnerId}/email-sending-domain`).then(r => r.data);

export const connectChannelPartnerEmailSendingDomain = (channelPartnerId, domain) =>
  apiClient
    .post(`/admin/channel-partners/${channelPartnerId}/email-sending-domain/connect`, { domain })
    .then(r => r.data);

export const verifyChannelPartnerEmailSendingDomain = (channelPartnerId) =>
  apiClient
    .post(`/admin/channel-partners/${channelPartnerId}/email-sending-domain/verify`)
    .then(r => r.data);

export const disconnectChannelPartnerEmailSendingDomain = (channelPartnerId) =>
  apiClient
    .post(`/admin/channel-partners/${channelPartnerId}/email-sending-domain/disconnect`)
    .then(r => r.data);

export const getChannelPartnerCustomDomainStatus = (channelPartnerId) =>
  apiClient.get(`/admin/channel-partners/${channelPartnerId}/custom-domain/status`).then(r => r.data);

export const registerChannelPartnerCustomDomain = (channelPartnerId, domainBase) =>
  apiClient
    .post(`/admin/channel-partners/${channelPartnerId}/custom-domain/register`, { domainBase })
    .then(r => r.data);

export const verifyChannelPartnerCustomDomain = (channelPartnerId) =>
  apiClient
    .post(`/admin/channel-partners/${channelPartnerId}/custom-domain/verify`)
    .then(r => r.data);

export const recheckChannelPartnerCustomDomain = (channelPartnerId) =>
  apiClient
    .post(`/admin/channel-partners/${channelPartnerId}/custom-domain/recheck`)
    .then(r => r.data);

export const disconnectChannelPartnerCustomDomain = (channelPartnerId) =>
  apiClient.delete(`/admin/channel-partners/${channelPartnerId}/custom-domain`).then(r => r.data);

export const getTenantMerchants = (id) =>
  apiClient.get(`/admin/tenants/${id}/merchants`).then(r => r.data);

export const updateTenantFeatures = (id, features) =>
  apiClient.put(`/admin/tenants/${id}/features`, { features }).then(r => r.data);

export const updateTenantBranding = (id, branding) =>
  apiClient.put(`/admin/tenants/${id}/branding`, { branding }).then(r => r.data);

export const uploadTenantLogo = (id, file) => {
  const formData = new FormData();
  formData.append('logo', file);
  return apiClient.post(`/admin/tenants/${id}/branding/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const uploadTenantFavicon = (id, file) => {
  const formData = new FormData();
  formData.append('favicon', file);
  return apiClient.post(`/admin/tenants/${id}/branding/favicon`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const deleteTenantLogo = (id) =>
  apiClient.delete(`/admin/tenants/${id}/branding/logo`).then(r => r.data);

export const deleteTenantFavicon = (id) =>
  apiClient.delete(`/admin/tenants/${id}/branding/favicon`).then(r => r.data);

export const uploadTenantLogoMark = (id, file) => {
  const formData = new FormData();
  formData.append('logoMark', file);
  return apiClient.post(`/admin/tenants/${id}/branding/logo-mark`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const deleteTenantLogoMark = (id) =>
  apiClient.delete(`/admin/tenants/${id}/branding/logo-mark`).then(r => r.data);

export const getTenantCustomDomainStatus = (id) =>
  apiClient.get(`/admin/tenants/${id}/custom-domain/status`).then(r => r.data);

export const registerTenantCustomDomain = (id, data) =>
  apiClient.post(`/admin/tenants/${id}/custom-domain/register`, data).then(r => r.data);

export const verifyTenantCustomDomain = (id) =>
  apiClient.post(`/admin/tenants/${id}/custom-domain/verify`).then(r => r.data);

export const disconnectTenantCustomDomain = (id) =>
  apiClient.delete(`/admin/tenants/${id}/custom-domain`).then(r => r.data);

// ── Channel Partners (LYN-288 / LYN-156) ─────────────────

export const getChannelPartners = () =>
  apiClient.get('/admin/channel-partners').then(r => r.data);

export const getChannelPartner = (id) =>
  apiClient.get(`/admin/channel-partners/${id}`).then(r => r.data);

export const updateChannelPartnerBranding = (id, branding) =>
  apiClient.put(`/admin/channel-partners/${id}/branding`, { branding }).then(r => r.data);

export const uploadChannelPartnerLogo = (id, file) => {
  const formData = new FormData();
  formData.append('logo', file);
  return apiClient.post(`/admin/channel-partners/${id}/branding/logo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const uploadChannelPartnerLogoMark = (id, file) => {
  const formData = new FormData();
  formData.append('logoMark', file);
  return apiClient.post(`/admin/channel-partners/${id}/branding/logo-mark`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const uploadChannelPartnerFavicon = (id, file) => {
  const formData = new FormData();
  formData.append('favicon', file);
  return apiClient.post(`/admin/channel-partners/${id}/branding/favicon`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const deleteChannelPartnerLogo = (id) =>
  apiClient.delete(`/admin/channel-partners/${id}/branding/logo`).then(r => r.data);

export const deleteChannelPartnerLogoMark = (id) =>
  apiClient.delete(`/admin/channel-partners/${id}/branding/logo-mark`).then(r => r.data);

export const deleteChannelPartnerFavicon = (id) =>
  apiClient.delete(`/admin/channel-partners/${id}/branding/favicon`).then(r => r.data);

export const updateChannelPartnerSignupCapabilities = (channelPartnerId, data) =>
  apiClient.patch(`/admin/channel-partners/${channelPartnerId}/signup-capabilities`, data)
    .then(r => r.data);

export const updateTenantSignupCapabilities = (salesOrgId, data) =>
  apiClient.patch(`/admin/tenants/${salesOrgId}/signup-capabilities`, data)
    .then(r => r.data);

export const createChannelPartner = (data, idempotencyKey) =>
  apiClient.post('/admin/channel-partners', data, {
    headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
  }).then(r => r.data);

// ── Switch-into overlay (LYN-157) — not /api/admin ───────

export const getSwitchOverlay = () =>
  apiClient.get('/switch').then(r => r.data);

export const startSwitchOverlay = ({ kind, id }) =>
  apiClient.post('/switch/start', { kind, id }).then(r => r.data);

export const stopSwitchOverlay = () =>
  apiClient.post('/switch/stop').then(r => r.data);

export const upSwitchOverlay = () =>
  apiClient.post('/switch/up').then(r => r.data);

// ── Platform Config ─────────────────────────────────────

export const getPlatformConfig = () =>
  apiClient.get('/admin/platform-config').then(r => r.data);

export const updatePlatformBranding = (branding) =>
  apiClient.put('/admin/platform-config/branding', { branding }).then(r => r.data);

export const updatePlatformFeatures = (features) =>
  apiClient.put('/admin/platform-config/features', { features }).then(r => r.data);

export const uploadPlatformLogo = (file) => {
  const formData = new FormData();
  formData.append('logo', file);
  return apiClient.post('/admin/platform-config/branding/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const uploadPlatformFavicon = (file) => {
  const formData = new FormData();
  formData.append('favicon', file);
  return apiClient.post('/admin/platform-config/branding/favicon', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }).then(r => r.data);
};

export const deletePlatformLogo = () =>
  apiClient.delete('/admin/platform-config/branding/logo').then(r => r.data);

export const deletePlatformFavicon = () =>
  apiClient.delete('/admin/platform-config/branding/favicon').then(r => r.data);

// ── Email Integration ────────────────────────────────────

export const getEmailConfig = () =>
  apiClient.get('/admin/integrations/email').then(r => r.data);

export const updateEmailConfig = (config) =>
  apiClient.put('/admin/integrations/email', config).then(r => r.data);

export const testEmailConnection = () =>
  apiClient.post('/admin/integrations/email/test').then(r => r.data);

// ── Platform Gateways ───────────────────────────────────

export const getPlatformGateways = () =>
  apiClient.get('/admin/integrations/platform-gateways').then(r => r.data);

export const getPlatformGateway = (id) =>
  apiClient.get(`/admin/integrations/platform-gateways/${id}`).then(r => r.data);

export const createPlatformGateway = (data) =>
  apiClient.post('/admin/integrations/platform-gateways', data).then(r => r.data);

export const updatePlatformGateway = (id, data) =>
  apiClient.put(`/admin/integrations/platform-gateways/${id}`, data).then(r => r.data);

export const deactivatePlatformGateway = (id) =>
  apiClient.patch(`/admin/integrations/platform-gateways/${id}/deactivate`).then(r => r.data);

// ── SO Gateways (per-tenant assignments) ─────────────────

export const getSOGateways = (salesOrgId) =>
  apiClient.get('/admin/integrations/gateways', { params: { salesOrgId } }).then(r => r.data);

export const createSOGateway = (data) =>
  apiClient.post('/admin/integrations/gateways', data).then(r => r.data);

export const updateSOGateway = (id, data) =>
  apiClient.put(`/admin/integrations/gateways/${id}`, data).then(r => r.data);

export const deactivateSOGateway = (id) =>
  apiClient.patch(`/admin/integrations/gateways/${id}/deactivate`).then(r => r.data);

// ── Templates ────────────────────────────────────────────

export const getTemplates = () =>
  apiClient.get('/admin/templates').then(r => r.data);

export const getTemplate = (id) =>
  apiClient.get(`/admin/templates/${id}`).then(r => r.data);

// ── CMS Sites / Direct Merchants (LYN-76) ────────────────

/**
 * List LynxPay-direct merchants with cms_sites hosting fields.
 * Pagination envelope: { success, data, pagination: { total, limit, offset, hasMore } }
 * @param {{ limit?: number, offset?: number }} [params]
 */
export const getDirectCmsMerchants = (params = {}) =>
  apiClient.get('/admin/cms-sites/direct-merchants', { params }).then(r => r.data);

/**
 * Flip websites hosting_tier for a company-owned cms_sites row.
 * @param {string} companyId
 * @param {{ hostingTier: 'free'|'paid' }} body
 */
export const updateCmsHostingTier = (companyId, body) =>
  apiClient
    .patch(`/admin/cms-sites/by-company/${companyId}/hosting-tier`, body)
    .then(r => r.data);

/**
 * Load a company plus Elavon assignment gate (LYN-221).
 * @param {string} companyId
 */
export const getAdminCompany = (companyId) =>
  apiClient.get(`/admin/companies/${companyId}`).then(r => r.data);

/**
 * LynxPay-direct platform company (not a Channel Partner). Super Admin Settings only.
 */
export const getPlatformActorCompany = () =>
  apiClient.get('/admin/companies/platform-actor').then(r => r.data);

/**
 * Create the LynxPay-the-platform company (create-if-missing).
 * @param {object} body
 */
export const createPlatformActorCompany = (body) =>
  apiClient.post('/admin/companies/platform-actor', body).then(r => r.data);

/**
 * Edit LynxPay Company Details (not merchantAlias).
 * @param {object} body
 */
export const updatePlatformActorCompany = (body) =>
  apiClient.patch('/admin/companies/platform-actor', body).then(r => r.data);

/**
 * Write companies.merchant_alias (allow-listed).
 * @param {string} companyId
 * @param {{ merchantAlias: string|null }} body
 */
export const updateAdminCompany = (companyId, body) =>
  apiClient.patch(`/admin/companies/${companyId}`, body).then(r => r.data);

// ── Audit Logs ───────────────────────────────────────────

export const getChannelPartnerSourceConnectors = (channelPartnerId) =>
  apiClient.get(`/admin/channel-partners/${channelPartnerId}/source-connectors`).then(r => r.data);

export const saveChannelPartnerSourceConnector = (channelPartnerId, connectorId, body) =>
  apiClient.put(`/admin/channel-partners/${channelPartnerId}/source-connectors/${connectorId}`, body).then(r => r.data);

export const uploadChannelPartnerSourceConnectorLogo = (channelPartnerId, connectorId, file) => {
  const form = new FormData();
  form.append('logo', file);
  return apiClient
    .post(`/admin/channel-partners/${channelPartnerId}/source-connectors/${connectorId}/logo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
    .then((r) => r.data);
};

export const getChannelPartnerSourceConnectorFlags = (channelPartnerId, connectorId) =>
  apiClient.get(`/admin/channel-partners/${channelPartnerId}/source-connectors/${connectorId}/flags`).then(r => r.data);

export const saveChannelPartnerSourceConnectorFlag = (channelPartnerId, connectorId, body) =>
  apiClient.put(`/admin/channel-partners/${channelPartnerId}/source-connectors/${connectorId}/flags`, body).then(r => r.data);

export const getTenantSourceConnectors = (salesOrgId) =>
  apiClient.get(`/admin/tenants/${salesOrgId}/source-connectors`).then(r => r.data);

export const saveTenantSourceConnector = (salesOrgId, connectorId, body) =>
  apiClient.put(`/admin/tenants/${salesOrgId}/source-connectors/${connectorId}`, body).then(r => r.data);

export const getAuditLogs = (params = {}) =>
  apiClient.get('/audit-logs', { params }).then(r => r.data);

export const getAuditLog = (id) =>
  apiClient.get(`/audit-logs/${id}`).then(r => r.data);
